import React, { useEffect, useMemo } from 'react';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api/ai-smart-button-store';
import { useDailyBriefUnread } from '@atlassian/ai-smart-button-api/state';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import type {
	PageRovoConfigProviderProps,
	RovoConfiguration,
	RovoConfigurationProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import { useNudgeContributorFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeContributorFilter.ts';
import { useNudgeFirstVisitFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeFirstVisitFilter.ts';
import { useAgentMentionSidebarListener } from '@confluence/agent-mention/entry-points/useAgentMentionSidebarListener.ts';

import { useNudgeRecommendationFilter } from './hooks/useNudgeRecommendationFilter.tsx';
import { useViewPageNudgeConfig } from './hooks/useViewPageNudgeConfig.ts';

const AgentMentionSidebarListener = () => {
	useAgentMentionSidebarListener();
	return null;
};

const ViewPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const { contentType, contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	const { isLoggedIn, isLicensed, cloudId } = useSessionData();
	const isSessionReady = isLoggedIn && isLicensed && !!cloudId;

	const viewPageRovoActions = useViewPageRovoActions();
	const { isBaseEligible, readTimeMinutes } = useNudgeRecommendationFilter(contentType, contentId);
	const { isCurrentUserContributor } = useNudgeContributorFilter(
		contentId,
		!isBaseEligible || !isSessionReady || !fg('aix_convo_starter_nudge_enabled'),
	);
	const { isFirstVisit } = useNudgeFirstVisitFilter(
		contentId,
		!isBaseEligible || !isSessionReady || !fg('aix_convo_starter_nudge_enabled'),
	);
	const dailyBriefStatus = useDailyBriefUnread();
	const nudges = useViewPageNudgeConfig(
		isBaseEligible,
		readTimeMinutes,
		isCurrentUserContributor,
		isFirstVisit,
		dailyBriefStatus,
	);
	const { setNudges } = useAISmartButtonActions();

	const rovoConfiguration = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);
				return Promise.resolve(actions);
			},
		};
	}, [viewPageRovoActions]);

	useEffect(() => {
		setConfiguration(rovoConfiguration);
		setNudges(nudges);
	}, [rovoConfiguration, setConfiguration, nudges, setNudges]);

	return expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) ? (
		<AgentMentionSidebarListener />
	) : null;
};

export const ViewPageRovoConfigProvider = (
	props: RovoConfigurationProviderProps,
): React.JSX.Element => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<ViewPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
